import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { invalidateReviewsCache, loadAllReviews } from '@api/reviewsCache';
import { type Review, type ReviewFilter } from '@api/types';

/** Legacy mock adapter key; safe to drop now that reviews use the backend API. */
const LEGACY_MOCK_STORAGE_KEY = 'smartname.reviews.v1';

export type ReviewsStatus = 'idle' | 'pending' | 'success' | 'error';

const applyReviewFilter = (items: readonly Review[], filter: ReviewFilter): Review[] => {
  if (filter === 'five') return items.filter((r) => r.rating === 5);
  if (filter === 'fourPlus') return items.filter((r) => r.rating >= 4);
  return [...items];
};

interface ReviewsContextValue {
  allItems: Review[];
  status: ReviewsStatus;
  error: Error | null;
  refresh: () => Promise<void>;
}

const ReviewsContext = createContext<ReviewsContextValue | null>(null);

export interface ReviewsProviderProps {
  children: ReactNode;
}

/** Fetches reviews once per session; consumers filter client-side. */
export const ReviewsProvider = ({ children }: ReviewsProviderProps) => {
  const [allItems, setAllItems] = useState<Review[]>([]);
  const [status, setStatus] = useState<ReviewsStatus>('idle');
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(async (force: boolean) => {
    setStatus('pending');
    setError(null);
    try {
      const items = await loadAllReviews(force);
      setAllItems(items);
      setStatus('success');
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      setStatus('error');
    }
  }, []);

  const refresh = useCallback(async () => {
    invalidateReviewsCache();
    await load(true);
  }, [load]);

  useEffect(() => {
    try {
      window.localStorage.removeItem(LEGACY_MOCK_STORAGE_KEY);
    } catch {
      // Storage disabled or unavailable.
    }
    void load(false);
  }, [load]);

  const value = useMemo(
    (): ReviewsContextValue => ({ allItems, status, error, refresh }),
    [allItems, status, error, refresh],
  );

  return <ReviewsContext.Provider value={value}>{children}</ReviewsContext.Provider>;
};

export const useReviewsContext = (): ReviewsContextValue => {
  const ctx = useContext(ReviewsContext);
  if (ctx === null) {
    throw new Error('useReviewsContext must be used within ReviewsProvider');
  }
  return ctx;
};

export const useFilteredReviews = (
  filter: ReviewFilter,
): ReviewsContextValue & { items: Review[] } => {
  const ctx = useReviewsContext();
  const items = useMemo(() => applyReviewFilter(ctx.allItems, filter), [ctx.allItems, filter]);
  return { ...ctx, items };
};
